import { useRef, useState } from 'react';
import AvatarEditor, { type AvatarEditorRef } from 'react-avatar-editor';
import { IconX, IconZoomIn, IconZoomOut } from '@tabler/icons-react';

interface AvatarZoomModalProps {
  isOpen: boolean;
  imageFile: File | null;
  onSave: (blob: Blob) => void;
  onClose: () => void;
}

const AvatarZoomModal: React.FC<AvatarZoomModalProps> = ({ isOpen, imageFile, onSave, onClose }) => {
  const editorRef = useRef<AvatarEditorRef>(null);
  const [scale, setScale] = useState(1.2);

  const handleSave = () => {
    if (!editorRef.current) return;
    editorRef.current.getImageScaledToCanvas().toBlob((blob) => {
      if (blob) { onSave(blob); onClose(); }
    }, 'image/jpeg', 0.9);
  };

  if (!isOpen || !imageFile) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60">
      <div className="bg-[var(--background-card)] rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-default)]">
          <p className="text-[16px] font-semibold text-[var(--text-content-title)]">Adjust Photo</p>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-[var(--background-card-elevated)] text-[var(--text-content-sub-title)] hover:bg-[var(--background-card-hover)] transition-colors"
          >
            <IconX size={16} />
          </button>
        </div>

        {/* Editor */}
        <div className="flex justify-center py-6 bg-[var(--background-page-body)]">
          <AvatarEditor
            ref={editorRef}
            image={imageFile}
            width={220}
            height={220}
            border={30}
            borderRadius={120}
            color={[0, 0, 0, 0.5]}
            scale={scale}
            rotate={0}
          />
        </div>

        {/* Zoom control */}
        <div className="px-6 py-4 flex items-center gap-3">
          <IconZoomOut size={18} className="text-[var(--text-content-sub-title)] flex-shrink-0" />
          <input
            type="range"
            min="1"
            max="3"
            step="0.01"
            value={scale}
            onChange={(e) => setScale(Number(e.target.value))}
            className="flex-1 accent-[var(--border-active)]"
          />
          <IconZoomIn size={18} className="text-[var(--text-content-sub-title)] flex-shrink-0" />
        </div>

        {/* Actions */}
        <div className="flex gap-3 px-5 pb-5">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-[var(--border-default)] text-[14px] font-medium text-[var(--text-content-title)] hover:bg-[var(--background-card-hover)] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex-1 py-2.5 rounded-xl bg-[var(--border-active)] text-white text-[14px] font-medium hover:opacity-90 transition-opacity"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default AvatarZoomModal;
